import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Network, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { departmentMasterAPI } from '../services/api';
import { getMasterCrudAccess } from '../lib/masterData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';

const AdminSubDepartmentsMasterPage = ({ routeBasePrefix = '/admin', dashboardHomePath = '/admin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const access = useMemo(() => getMasterCrudAccess(user), [user]);
  const departmentsPath = `${routeBasePrefix}/master/departments`;
  const subDepartmentsPath = `${routeBasePrefix}/master/subdepartments`;
  const designationsPath = `${routeBasePrefix}/master/designations`;
  const importPath = `${routeBasePrefix}/import/sub_department_master`;

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await departmentMasterAPI.list({ include_inactive: includeInactive });
      setRows(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load sub-departments'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const subDepartmentRows = useMemo(
    () => rows.flatMap((dept) => (dept.sub_departments || []).map((sub) => ({ ...sub, department_id: dept.id, department_name: dept.name }))),
    [rows]
  );

  const filteredRows = useMemo(() => {
    if (departmentFilter === 'all') return subDepartmentRows;
    return subDepartmentRows.filter((row) => String(row.department_id) === String(departmentFilter));
  }, [subDepartmentRows, departmentFilter]);

  useEffect(() => {
    const successMessage = location.state?.masterSuccessMessage;
    if (!successMessage) return;
    toast.success(successMessage);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Sub-Department Master</h2>
          <p className="text-sm text-muted-foreground">Manage sub-departments with department filter.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate(importPath)}>Import Sub-Departments</Button>
          <Button variant="outline" onClick={() => navigate(departmentsPath)}>Departments</Button>
          <Button variant="outline" onClick={() => navigate(designationsPath)}>Designations</Button>
          {access.canCreate ? (
            <Button onClick={() => navigate(`${subDepartmentsPath}/new`)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sub-Department
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => navigate(dashboardHomePath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <StatCard label="Total Sub-Departments" value={filteredRows.length} icon={Network} tone="primary" />

      <Card>
        <CardHeader><CardTitle>Sub-Department Table</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <Checkbox id="subdept-include-inactive" checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(Boolean(v))} />
              <Label htmlFor="subdept-include-inactive">Show Inactive</Label>
            </div>
            <div>
              <Label>Department Filter</Label>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger><SelectValue placeholder="All Departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {rows.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>{row.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead>Sub-Department</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <CardSkeleton rows={5} />
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <EmptyState
                      icon={Network}
                      title="No sub-departments found"
                      description="Adjust the filter or create a sub-department to get started."
                      {...(access.canCreate ? { actionLabel: 'Add Sub-Department', onAction: () => navigate(`${subDepartmentsPath}/new`) } : {})}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {filteredRows.map((row) => (
                <TableRow key={row.id} className="card-interactive">
                  <TableCell>{row.department_name}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Badge variant={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {access.canView ? (
                        <Button size="icon" variant="outline" title="View" onClick={() => navigate(`${subDepartmentsPath}/${row.id}/view`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {access.canEdit ? (
                        <Button size="icon" variant="outline" title="Edit" onClick={() => navigate(`${subDepartmentsPath}/${row.id}/edit`)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {access.canDelete ? (
                        <Button size="icon" variant="outline" title="Delete" onClick={() => navigate(`${subDepartmentsPath}/${row.id}/delete`)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSubDepartmentsMasterPage;
